import { Controller } from '@hotwired/stimulus'

/**
 * Secure Token Controller
 *
 * Handles secure display of sensitive tokens (like API keys) with:
 * - Auto-clear from DOM after countdown expires
 * - Copy to clipboard with confirmation
 * - Token cleared immediately after successful copy
 */
export default class extends Controller {
  static targets = [
    'token',
    'countdown',
    'copyButton',
    'container',
    'copiedMessage']

  static values = {
    duration: { type: Number, default: 60 }
  }

  connect () {
    if (this.hasTokenTarget) {
      this.remainingSeconds = this.durationValue
      this.updateCountdown()
      this.startCountdown()
    } else {
      this.remainingSeconds = 0
      this.updateCountdown()
    }
  }

  disconnect () {
    this.clearToken()
    this.stopCountdown()
  }

  startCountdown () {
    this.timer = setInterval(() => {
      this.remainingSeconds--
      this.updateCountdown()

      if (this.remainingSeconds <= 0) {
        this.clearToken()
      }
    }, 1000)
  }

  stopCountdown () {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  updateCountdown () {
    if (this.hasCountdownTarget) {
      this.countdownTarget.textContent = this.remainingSeconds
    }
  }

  async copy (event) {
    event.preventDefault()

    if (!this.hasTokenTarget) {
      return
    }

    const token = this.tokenTarget.textContent.trim()

    try {
      await navigator.clipboard.writeText(token)
      this.showCopiedConfirmation()
    } catch (err) {
      console.error('Failed to copy token:', err)
      // Fallback for older browsers
      this.fallbackCopy(token)
    }
  }

  fallbackCopy (text) {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    document.body.appendChild(textArea)
    textArea.select()

    try {
      document.execCommand('copy')
      this.showCopiedConfirmation()
    } catch (err) {
      console.error('Fallback copy failed:', err)
    }

    document.body.removeChild(textArea)
  }

  showCopiedConfirmation () {
    if (this.hasCopiedMessageTarget) {
      this.copiedMessageTarget.classList.remove('hidden')
      setTimeout(() => this.copiedMessageTarget.classList.add('hidden'), 3000)
    }
  }

  clearToken () {
    this.stopCountdown()

    if (this.hasContainerTarget) {
      // Replace the token display with a cleared message
      this.containerTarget.innerHTML = `
        <div class="flex items-center text-sm text-gray-600">
          <svg class="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span>Token cleared from display for security. It has been saved securely.</span>
        </div>
      `
    }
  }
}
